/*
 * Hexlox Product Finder — adding to the cart.
 *
 * Sends the lines built by hexlox-finder-engine.js to Shopify's cart (Ajax Cart API) and reports
 * exactly what was added and what was not. No DOM, no shared state: every call gets its own
 * `fetch`, so the Node tests can run it against a simulated cart.
 *
 *   HexloxFinderCart.add(opts) -> Promise<result>
 *
 *   opts.fetch        window.fetch (or a test double)
 *   opts.root         window.Shopify.routes.root, e.g. "/" or "/en/"
 *   opts.build(cart)  -> { items, attributes }  (engine.buildCart with cartHasKey from the cart)
 *   opts.codeAttribute  name of the Hexlox Code attribute ("Hexlox Code"); only it counts for codeSaved
 *   opts.available(variantId) -> true | false | undefined (unknown)
 *   opts.skipUnavailable  false: stop before adding anything if a line is sold out
 *                         true:  add the other lines, report the sold-out ones as not added
 *
 *   result.status   'ok'          everything added (and the Hexlox Code saved, if filled)
 *                   'unavailable' nothing added yet; result.unavailable lists the sold-out lines
 *                   'partial'     some lines added; result.failed lists the others, with Shopify's reason
 *                   'failed'      nothing added; result.message has Shopify's reason if there was one
 *   result.added    lines that are now in the cart
 *   result.failed   [{ item, reason: 'sold_out' | 'rejected', message }]
 *   result.codeSaved  true / false, or null when no Hexlox Code was filled in (other notes don't count)
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.HexloxFinderCart = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function request(opts, path, body) {
    var init = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    if (body) {
      init.method = 'POST';
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    return opts.fetch(opts.root + path, init).then(function (r) {
      return r.json().catch(function () { return null; }).then(function (json) {
        return { ok: r.ok, status: r.status, json: json };
      });
    });
  }

  function getCart(opts) {
    return request(opts, 'cart.js').then(function (r) {
      if (!r.ok || !r.json) throw new Error('cart.js ' + r.status);
      return r.json;
    });
  }

  // Shopify's error text ("… is already sold out.", "You can only add 3 …"), in the shop's language.
  function shopifyMessage(r) {
    var j = r && r.json;
    return (j && (j.description || j.message)) || '';
  }

  function totals(cart) {
    var t = {};
    (cart.items || []).forEach(function (i) {
      var id = Number(i.variant_id || i.id);
      t[id] = (t[id] || 0) + Number(i.quantity || 0);
    });
    return t;
  }

  // After a failed or interrupted multi-line add, find out which lines Shopify did keep, by comparing
  // the cart before and after. Lines are matched in order, per variant.
  function alreadyAdded(items, before, after) {
    var b = totals(before), a = totals(after), grown = {};
    Object.keys(a).forEach(function (id) { grown[id] = Math.max(0, a[id] - (b[id] || 0)); });
    return items.map(function (item) {
      var id = Number(item.id);
      if ((grown[id] || 0) >= item.quantity) { grown[id] -= item.quantity; return true; }
      return false;
    });
  }

  // Add lines one by one, so one refused line cannot block the others.
  function addEach(opts, items) {
    var added = [], failed = [];
    return items.reduce(function (chain, item) {
      return chain.then(function () {
        return request(opts, 'cart/add.js', { items: [item] }).then(function (r) {
          if (r.ok) added.push(item);
          else failed.push({ item: item, reason: 'rejected', message: shopifyMessage(r) });
        }, function () {
          failed.push({ item: item, reason: 'rejected', message: '' });
        });
      });
    }, Promise.resolve()).then(function () { return { added: added, failed: failed }; });
  }

  function add(opts) {
    var before, payload, unavailable, toSend;

    return getCart(opts).then(function (cart) {
      before = cart;
      payload = opts.build(cart);
      unavailable = payload.items.filter(function (i) { return opts.available(i.id) === false; });
      toSend = payload.items.filter(function (i) { return unavailable.indexOf(i) === -1; });

      if (!payload.items.length) return { status: 'failed', added: [], failed: [], codeSaved: null, message: '' };
      if (unavailable.length && !opts.skipUnavailable) {
        return { status: 'unavailable', added: [], failed: [], unavailable: unavailable, codeSaved: null };
      }
      var soldOut = unavailable.map(function (item) { return { item: item, reason: 'sold_out', message: '' }; });
      if (!toSend.length) return { status: 'failed', added: [], failed: soldOut, codeSaved: null, message: '' };

      // 1. All lines in one request: fast, and the cart drawer shows them together.
      return request(opts, 'cart/add.js', { items: toSend }).then(function (r) {
        if (r.ok) return { added: toSend.slice(), failed: [], batchMessage: '' };
        return fallback(shopifyMessage(r));
      }, function () {
        return fallback('');
      }).then(function (res) {
        res.failed = soldOut.concat(res.failed);
        return saveCode(res);
      });
    });

    // 2. Shopify refused the batch (e.g. one line ran out of stock since the page loaded) or the
    //    connection dropped. Check what is in the cart now, then add the rest line by line.
    function fallback(batchMessage) {
      return getCart(opts).then(function (after) {
        var done = alreadyAdded(toSend, before, after);
        var rest = toSend.filter(function (_, i) { return !done[i]; });
        return addEach(opts, rest).then(function (each) {
          return {
            added: toSend.filter(function (_, i) { return done[i]; }).concat(each.added),
            failed: each.failed,
            batchMessage: batchMessage
          };
        });
      });
    }

    // 3. Order-level cart attributes, only sent when something was added: the Hexlox Code (if typed), the
    //    "I already own" notes (Key, Inserts) and the hidden _finder note. Only the code the customer typed
    //    counts for codeSaved; a staff note that fails to save is not the customer's problem.
    function saveCode(res) {
      var attrs = payload.attributes || {};
      var hasCode = !!(opts.codeAttribute && attrs[opts.codeAttribute]);
      var hasAny = Object.keys(attrs).length > 0;
      var finish = function (codeSaved) {
        var status = !res.added.length ? 'failed'
          : res.failed.length || codeSaved === false ? 'partial' : 'ok';
        var firstMessage = res.failed.map(function (f) { return f.message; }).filter(Boolean)[0];
        return {
          status: status,
          added: res.added,
          failed: res.failed,
          codeSaved: codeSaved,
          message: res.batchMessage || firstMessage || ''
        };
      };
      if (!hasAny || !res.added.length) return finish(hasCode ? false : null);
      var update = function () { return request(opts, 'cart/update.js', { attributes: attrs }); };
      // One retry: the lines are already in the cart, so losing the code would be a quiet data loss.
      return update().then(function (r) { return r.ok ? r : update(); }, update).then(function (r) {
        return finish(hasCode ? !!(r && r.ok) : null);
      }, function () {
        return finish(hasCode ? false : null);
      });
    }
  }

  return { add: add, alreadyAdded: alreadyAdded };
});
